/**
 * The rules engine. Pure: given a transaction and an ordered rule list, decide
 * which category it belongs to.
 *
 * First match wins. Ordering is the user's lever — a specific rule placed above
 * a general one overrides it, which is easier to reason about than a scoring
 * system where nobody can explain why a row landed where it did.
 */

import type { Rule, RuleMatch, Transaction } from '../domain/types.ts';

/** The subset of a transaction a rule can see. */
export type Matchable = Pick<
  Transaction,
  'merchantNormalized' | 'merchantRaw' | 'amountILS' | 'cardId' | 'originalCurrency' | 'extraDetails'
>;

export interface Assignment {
  categoryId: string;
  subcategoryId: string | null;
  tags: string[];
  /** Which rule produced this, for "why is this row here?". */
  ruleId: string;
}

/**
 * A regex from user input can be malformed or catastrophically slow. Compiling
 * it once per evaluation and swallowing failures keeps one bad rule from
 * breaking categorization for every other row.
 */
const regexCache = new Map<string, RegExp | null>();

function compile(pattern: string, caseSensitive: boolean): RegExp | null {
  const key = `${caseSensitive ? 's' : 'i'}:${pattern}`;
  const cached = regexCache.get(key);
  if (cached !== undefined) return cached;

  let compiled: RegExp | null = null;
  try {
    compiled = new RegExp(pattern, caseSensitive ? 'u' : 'iu');
  } catch {
    compiled = null;
  }
  regexCache.set(key, compiled);
  return compiled;
}

function matchesMerchant(match: NonNullable<RuleMatch['merchant']>, tx: Matchable): boolean {
  const caseSensitive = match.caseSensitive ?? false;

  if (match.mode === 'regex') {
    const regex = compile(match.value, caseSensitive);
    // An invalid pattern matches nothing rather than everything: a broken rule
    // must not silently swallow the whole statement.
    return regex === null ? false : regex.test(tx.merchantNormalized);
  }

  // merchantNormalized is already uppercased and punctuation-folded, so a
  // substring rule is compared against the same normalized form the user sees
  // grouped in the merchants report.
  const needle = caseSensitive ? match.value : match.value.toUpperCase();
  const haystack = caseSensitive ? tx.merchantRaw : tx.merchantNormalized;
  return needle.length > 0 && haystack.includes(needle);
}

/**
 * Whether a merchant matcher's pattern actually compiles.
 *
 * A malformed regex is deliberately made to match nothing rather than
 * everything — the safe failure — but that makes the editor's live count read
 * 0 for both "this pattern is broken" and "this pattern is fine, nothing
 * matches it yet". Only the editor needs to tell those apart; matching itself
 * stays fail-safe. Substring patterns cannot fail to compile.
 */
export function isValidMerchantPattern(merchant: NonNullable<RuleMatch['merchant']>): boolean {
  if (merchant.mode !== 'regex') return true;
  return compile(merchant.value, merchant.caseSensitive ?? false) !== null;
}

export function ruleMatches(rule: Rule, tx: Matchable): boolean {
  if (!rule.enabled) return false;
  const { match } = rule;

  if (match.merchant !== undefined && !matchesMerchant(match.merchant, tx)) return false;

  if (match.amount !== undefined) {
    // Compare on magnitude: a 200-shekel refund should match the same rule as
    // a 200-shekel charge, otherwise refunds escape their category.
    const amount = Math.abs(tx.amountILS);
    if (match.amount.min !== undefined && amount < match.amount.min) return false;
    if (match.amount.max !== undefined && amount > match.amount.max) return false;
  }

  if (match.cardId !== undefined && match.cardId !== tx.cardId) return false;

  if (match.currency !== undefined) {
    // A row with no original currency is an ILS row.
    const currency = tx.originalCurrency ?? 'ILS';
    if (currency !== match.currency) return false;
  }

  if (match.detailsContains !== undefined) {
    const details = tx.extraDetails ?? '';
    if (!details.includes(match.detailsContains)) return false;
  }

  return true;
}

/** First matching rule wins. Returns null when nothing matches. */
export function categorize(tx: Matchable, rules: readonly Rule[]): Assignment | null {
  const ordered = [...rules].sort((a, b) => a.order - b.order);

  for (const rule of ordered) {
    if (!ruleMatches(rule, tx)) continue;
    return {
      categoryId: rule.assign.categoryId,
      subcategoryId: rule.assign.subcategoryId ?? null,
      tags: rule.assign.tags ?? [],
      ruleId: rule.id,
    };
  }
  return null;
}

export interface RecategorizeResult {
  /** Transactions whose category actually changed. */
  changed: Array<{ transaction: Transaction; assignment: Assignment }>;
  unmatched: number;
}

/**
 * Apply rules across a set of transactions.
 *
 * `respectManual` defaults to true: a category a human chose by hand outranks
 * any rule. Re-running rules must never quietly undo the user's own work — that
 * is the difference between a helpful automation and one you stop trusting.
 */
export function recategorize(
  transactions: readonly Transaction[],
  rules: readonly Rule[],
  respectManual = true,
): RecategorizeResult {
  const changed: RecategorizeResult['changed'] = [];
  let unmatched = 0;

  for (const transaction of transactions) {
    if (respectManual && transaction.manuallyEdited && transaction.categoryId !== null) continue;

    const assignment = categorize(transaction, rules);
    if (assignment === null) {
      if (transaction.categoryId === null) unmatched += 1;
      continue;
    }

    if (
      transaction.categoryId === assignment.categoryId &&
      transaction.subcategoryId === assignment.subcategoryId
    ) {
      continue;
    }

    changed.push({ transaction, assignment });
  }

  return { changed, unmatched };
}

/** Next order value, so a new rule lands at the end of the list. */
export function nextRuleOrder(rules: readonly Rule[]): number {
  return rules.reduce((max, rule) => Math.max(max, rule.order), -1) + 1;
}

/**
 * Order value that puts a new rule in front of every existing one.
 *
 * A rule someone just wrote by hand is about one merchant they had in mind, so
 * it should beat the shipped generic patterns rather than queue up behind
 * forty of them. It also means a new rule is visible where they are looking
 * instead of at the bottom of a long list.
 *
 * Derived from the minimum rather than the count: two rules created before the
 * list reloads would otherwise be handed the same order, and a tie hands the
 * decision to Dexie's insertion order.
 */
export function frontRuleOrder(rules: readonly Rule[]): number {
  return rules.reduce((min, rule) => Math.min(min, rule.order), 0) - 1;
}

/**
 * Whether a rule constrains anything at all.
 *
 * `ruleMatches` skips every check its `match` does not define, so an empty
 * match object falls through all of them and returns true — a rule with no
 * condition claims every transaction in the account. Editing is the way that
 * gets created (clear the merchant pattern and save), so the editor has to
 * refuse it. A merchant matcher whose value is blank counts as absent: the
 * substring path already treats it as matching nothing.
 */
export function hasMatcher(match: RuleMatch): boolean {
  if (match.merchant !== undefined && match.merchant.value.trim().length > 0) return true;
  return (
    match.amount !== undefined ||
    match.cardId !== undefined ||
    match.currency !== undefined ||
    match.detailsContains !== undefined
  );
}

/**
 * Move a rule so it lands immediately before position `beforeIndex`,
 * renumbering the list.
 *
 * Order is the user's only lever over which rule wins, so it has to be
 * editable — the settings hint tells people to put specific rules above
 * general ones, and until now nothing could.
 *
 * `beforeIndex` indexes the list as it stands *now*, and `rules.length` means
 * "past the last one". Those are exactly the two values a drop target produces
 * — the top half of row i gives i, the bottom half gives i+1 — so the
 * adjustment for the gap left behind by the moved rule happens here, in one
 * tested place, rather than as an off-by-one in the component.
 *
 * Renumbering to 0..n-1 rather than swapping `order` values is deliberate.
 * Rules created outside the list are inserted at negative orders to land in
 * front, so the sequence arrives full of gaps and, once two of them collide,
 * duplicates. Two rules sharing an order makes "first match wins" fall back to
 * Dexie's insertion order — an invisible tiebreak nobody chose. The first
 * reorder normalizes all of it.
 *
 * Returns only the rules whose order actually changed, so the caller writes the
 * minimum; a move that changes nothing returns nothing.
 */
export function moveRuleBefore(
  rules: readonly Rule[],
  id: string,
  beforeIndex: number,
): Rule[] {
  const ordered = [...rules].sort((a, b) => a.order - b.order);
  const from = ordered.findIndex((rule) => rule.id === id);
  if (from === -1) return [];

  const clamped = Math.max(0, Math.min(beforeIndex, ordered.length));
  // Dropped on its own top or bottom edge: both mean "stay where you are", and
  // a no-op must not rewrite the list.
  if (clamped === from || clamped === from + 1) return [];

  const [moved] = ordered.splice(from, 1);
  ordered.splice(clamped > from ? clamped - 1 : clamped, 0, moved as Rule);

  const changed: Rule[] = [];
  ordered.forEach((rule, position) => {
    if (rule.order !== position) changed.push({ ...rule, order: position });
  });
  return changed;
}
